import { useState, useMemo, createContext, useContext, ReactNode } from "react"
import { useDispatch } from "react-redux"
import { AxiosError } from "axios"

import { Spinner } from "components/Spinner"
import { logEvent } from "utils/helpers"
import { ru } from "utils/constants"
import { useDidMount } from "utils/customHooks"
import { httpClient } from "utils/infrastructure/httpClient"
import { Layout } from "components"
import { setPsyHash } from "utils/store/slices"
import { AppDispatch } from "utils/store/store"

const DEFAULT_ERROR_MESSAGE = "Ошибка при входе через аккаунт Google"

const ERRORS: { [key: number]: string } = {
    401: "Ошибка авторизации. Попробуйте выйти и снова войти в систему через аккаунт Google.",
    403: "Недостаточно прав для просмотра. Попробуйте выйти и снова войти в систему через аккаунт Google.",
    500: ru.serverErrorMessage,
}
// const REDIRECT_MESSAGE =
//     "Ваша сессия истекла. Сейчас вы будете перенаправлены на страницу авторизации."

interface Value {
    loadingState: "idle" | "loading" | "ready"
    isSignedIn: boolean
    errorMessage?: string
}

interface Handlers {
    authByCode(code: string): void
    signOut(): void
    signIn(this: void, data: VerifyResponse): void
    onSignInFailure(
        this: void,
        reason?: AxiosError & { response: { data: { message: string } } }
    ): void
    verify(this: void): void
}

interface Props {
    children: ReactNode
}

interface VerifyResponse {
    hash_id: string
    result: string[]
    redirected: boolean
    url: string
}

export const AuthContext = createContext<[Handlers, Value] | null>(null)

export function AuthContextProvider({ children }: Props) {
    const dispatch = useDispatch<AppDispatch>()
    const [isSignedIn, setIsSignedIn] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [loadingState, setLoadingState] = useState<Value["loadingState"]>("idle")

    useDidMount(() => {
        setLoadingState("loading")
        handlers.verify()
    })

    const handlers: Handlers = useMemo(
        () => ({
            authByCode(code: string) {
                setLoadingState("loading")
                httpClient
                    .post<VerifyResponse>("psychologist/auth/auth-by-code", { auth_code: code })
                    .then(handlers.signIn)
                    .catch(handlers.onSignInFailure)
                    .finally(() => {
                        setLoadingState("ready")
                    })
            },
            signOut(): void {
                setIsSignedIn(false)
                void httpClient.get("psychologist/auth/logout")
                logEvent({ event: "PsyProfile signed out" })
            },
            signIn(data: VerifyResponse): void {
                dispatch(setPsyHash(data.hash_id))
                if (data.redirected) {
                    window.location.href = data.url
                }
                setIsSignedIn(true)
                logEvent({ event: "PsyProfile signed in" })
                setLoadingState("ready")
            },
            onSignInFailure(error): void {
                setIsSignedIn(false)
                setErrorMessage(
                    error?.status && error.status in ERRORS
                        ? ERRORS[error.status]
                        : DEFAULT_ERROR_MESSAGE
                )
                setLoadingState("ready")
            },
            verify() {
                return httpClient
                    .get<VerifyResponse>("psychologist/auth/verify")
                    .then(handlers.signIn)
                    .catch(handlers.onSignInFailure)
            },
        }),
        []
    )

    if (loadingState === "ready")
        return (
            <AuthContext.Provider
                value={[
                    handlers,
                    {
                        loadingState,
                        isSignedIn,
                        errorMessage,
                    },
                ]}
            >
                {children}
            </AuthContext.Provider>
        )
    return (
        <Layout>
            <Spinner />
        </Layout>
    )
}

export function useAuthContext() {
    const currentContext = useContext(AuthContext)

    if (!currentContext) {
        throw new Error("useAuthContext has to be used within <AuthContext.Provider>")
    }

    return currentContext
}
